import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, formatDate } from "../lib/api";
import { Check, CloudUpload, Loader2, Download, Database, Link2, RefreshCw, Bell, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { GstVerifyField } from "../components/GstVerifyField";
import { SystemHealth } from "../components/SystemHealth";

function PublicExpenseLink() {
  const [link, setLink] = useState("");
  const load = useCallback(() => api.get("/public-expense/link").then((r) => setLink(`${window.location.origin}${r.data.path}`)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  async function regen() {
    if (!confirm("Generate a new link? The old link will stop working.")) return;
    const { data } = await api.post("/public-expense/regenerate");
    setLink(`${window.location.origin}${data.path}`);
    toast.success("New public expense link generated");
  }
  function copy() { navigator.clipboard.writeText(link); toast.success("Link copied"); }
  return (
    <div className="card-surface p-6 mt-8" data-testid="public-expense-link-card">
      <div className="section-title flex items-center gap-2"><Link2 className="w-4 h-4" /> Public Expense Link</div>
      <p className="text-sm text-[#68665E] max-w-xl mb-3">Share this link with drivers/staff. Anyone with it can submit an expense against an LR number (no login). It will reflect on that LR automatically.</p>
      <div className="flex flex-wrap items-center gap-2">
        <input readOnly value={link} className="input-field flex-1 min-w-[260px] font-mono text-xs" data-testid="public-expense-link-input" />
        <button onClick={copy} className="btn-secondary" data-testid="copy-expense-link"><Link2 className="w-4 h-4" /> Copy</button>
        <a href={link} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="open-expense-link">Open</a>
        <button onClick={regen} className="btn-ghost text-sm" data-testid="regen-expense-link"><RefreshCw className="w-4 h-4" /> Regenerate</button>
      </div>
    </div>
  );
}

function DataBackup() {
  const [backups, setBackups] = useState([]);
  const [running, setRunning] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = useCallback(() => api.get("/drive/files", { params: { category: "backup" } }).then((r) => setBackups(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  async function runBackup() {
    setRunning(true);
    try {
      const { data } = await api.post("/drive/backup");
      const total = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);
      toast.success(`Backup saved to Drive — ${total} records across ${Object.keys(data.counts).length} tables`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Backup failed");
    } finally {
      setRunning(false);
    }
  }

  async function purgeOld() {
    if (!confirm("Delete all Drive backups older than 7 days? This cannot be undone.")) return;
    setPurging(true);
    try {
      const { data } = await api.post("/drive/purge-backups");
      toast.success(`Removed ${data.removed} backup(s) older than ${data.retention_days} days`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Purge failed");
    } finally {
      setPurging(false);
    }
  }

  async function download(b) {
    try {
      const res = await api.get(`/drive/files/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = b.name; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  }

  return (
    <div className="card-surface p-6 mt-8" data-testid="data-backup-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="section-title flex items-center gap-2"><Database className="w-4 h-4" /> Full Data Backup to Google Drive</div>
          <p className="text-sm text-[#68665E] max-w-xl">Exports <strong>everything</strong> — users, LRs, invoices, expenses, customers, contacts, departments, vehicles, drivers and the activity log — into a single Excel workbook stored in your Drive "Data Backups" folder.</p>
          <p className="text-xs text-[#2F6F4E] mt-2 inline-flex items-center gap-1" data-testid="auto-backup-note"><Database className="w-3 h-3" /> Auto-backup runs <strong>every hour</strong>; backups older than <strong>7 days</strong> are deleted automatically.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={purgeOld} disabled={purging} className="btn-secondary" data-testid="purge-backups-button">
            {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
            {purging ? "Cleaning…" : "Delete old (>7d)"}
          </button>
          <button onClick={runBackup} disabled={running} className="btn-primary" data-testid="run-backup-button">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />}
            {running ? "Backing up…" : "Back Up Now"}
          </button>
        </div>
      </div>

      <div className="mt-5">
        <div className="text-xs uppercase tracking-wide text-[#68665E] mb-2">Recent Backups</div>
        {backups.length === 0 ? (
          <div className="text-sm text-[#A09E96]">No backups yet.</div>
        ) : (
          <div className="border border-[#E5E3DB] divide-y divide-[#E5E3DB]">
            {backups.map((b) => (
              <div key={b.id} className="flex items-center gap-3 px-3 py-2" data-testid={`backup-row-${b.id}`}>
                <Database className="w-4 h-4 text-[#C84B31] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{b.name}</div>
                  <div className="text-[11px] text-[#68665E]">{(b.size / 1024).toFixed(0)} KB · {formatDate(b.uploaded_at)} {String(b.uploaded_at).slice(11, 16)}</div>
                </div>
                {b.web_view_link && <a href={b.web_view_link} target="_blank" rel="noreferrer" className="btn-ghost text-xs">Open</a>}
                <button className="btn-ghost" onClick={() => download(b)} data-testid={`backup-download-${b.id}`}><Download className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReminderNudges() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [lsoStaff, setLsoStaff] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/nudges/preview").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    api.get("/garv/users").then((r) => setUsers(r.data || [])).catch(() => {});
    api.get("/nudges/lso-staff").then((r) => setLsoStaff(r.data?.staff_id || "")).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  async function saveLsoStaff(id) {
    setLsoStaff(id);
    try {
      const { data: res } = await api.put("/nudges/lso-staff", { staff_id: id });
      toast.success(res.staff_name ? `L&T LSO reminders will go to ${res.staff_name}` : "L&T LSO reminders reset to the board only");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
    }
  }

  async function sendNow() {
    if (!confirm("Send today's reminder digest on WhatsApp to all admins now, and add cards to the board?")) return;
    setSending(true);
    try {
      const { data: res } = await api.post("/nudges/run?send=true");
      toast.success(`Nudges run — ${res.messages_sent} WhatsApp message(s) sent, ${res.invoice_nudges} invoice + ${res.ewb_nudges} EWB reminder(s)`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not run nudges");
    } finally { setSending(false); }
  }

  const invCount = data?.invoices_due_today?.length || 0;
  const ewbCount = data?.ewbs_due_today?.length || 0;
  const lsoCount = data?.lsos_due_today?.length || 0;

  return (
    <div className="card-surface p-6 mt-8" data-testid="reminder-nudges-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="section-title flex items-center gap-2"><Bell className="w-4 h-4" /> Daily Reminder Nudges</div>
          <p className="text-sm text-[#68665E] max-w-xl">Every day at <strong>9:00 AM IST</strong> I WhatsApp all admins (with a saved WhatsApp number) to follow up on <strong>overdue invoices</strong> (on the due date, then every 3 days until paid) and <strong>E-way bills expiring</strong> in 2 / 1 / 0 days. Each item also appears on the Reminders board. Customers are never messaged.</p>
        </div>
        <button onClick={sendNow} disabled={sending} className="btn-primary" data-testid="nudges-send-now">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? "Sending…" : "Send now"}
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="nudges-summary">
        <div className="border border-[#E5E3DB] rounded p-3">
          <div className="text-2xl font-bold text-[#1A1A18]" data-testid="nudges-invoice-count">{loading ? "…" : invCount}</div>
          <div className="text-xs text-[#68665E]">Invoice follow-ups due today</div>
        </div>
        <div className="border border-[#E5E3DB] rounded p-3">
          <div className="text-2xl font-bold text-[#1A1A18]" data-testid="nudges-ewb-count">{loading ? "…" : ewbCount}</div>
          <div className="text-xs text-[#68665E]">E-way bills expiring soon</div>
        </div>
        <div className="border border-[#E5E3DB] rounded p-3">
          <div className="text-2xl font-bold text-[#1A1A18]" data-testid="nudges-recipient-count">{loading ? "…" : (data?.recipient_count || 0)}</div>
          <div className="text-xs text-[#68665E]">Admin recipient(s)</div>
        </div>
      </div>

      <div className="mt-5 border border-[#E5E3DB] rounded p-4" data-testid="nudges-lso-staff-card">
        <div className="text-sm font-semibold text-[#1A1A18] mb-1">L&amp;T LSO reminders → assigned staff</div>
        <p className="text-xs text-[#68665E] mb-2">Reminders for L&amp;T LSOs closing soon go to <strong>this one person</strong> (their board card + WhatsApp) instead of all admins. {lsoCount > 0 && <span>{lsoCount} LSO(s) closing today.</span>}</p>
        <select className="input-field !py-1.5 text-sm max-w-xs" value={lsoStaff} onChange={(e) => saveLsoStaff(e.target.value)} data-testid="nudges-lso-staff-select">
          <option value="">— Board only (no one assigned) —</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}{u.role ? ` (${u.role})` : ""}</option>)}
        </select>
        {data?.lso_staff_name && !data?.lso_staff_has_whatsapp && (
          <p className="text-xs text-[#8A2526] mt-2" data-testid="nudges-lso-no-whatsapp">{data.lso_staff_name} has no WhatsApp number saved — add one on the Users page for WhatsApp delivery (board card still works).</p>
        )}
      </div>

      {(data?.recipient_count === 0) && (
        <p className="text-xs text-[#8A2526] mt-3" data-testid="nudges-no-recipients">No admin has a WhatsApp number saved yet — add one on the Users page so nudges can be delivered.</p>
      )}
      {data?.message_preview ? (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-wide text-[#68665E] mb-2">Today's message preview</div>
          <pre className="text-xs text-[#4A4842] whitespace-pre-wrap border border-[#E5E3DB] rounded p-3 bg-[#F5F4F0]" data-testid="nudges-message-preview">{data.message_preview}</pre>
        </div>
      ) : (
        !loading && <p className="text-sm text-[#A09E96] mt-4" data-testid="nudges-nothing-today">Nothing due today — nobody will be messaged.</p>
      )}
    </div>
  );
}

export default function Settings() {
  const [form, setForm] = useState({
    name: "", address: "", phone: "", email: "",
    gst_number: "", pan_number: "",
    bank_name: "", bank_account: "", bank_ifsc: "",
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get("/company").then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(()=>{});
  }, []);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/company", form);
      setSavedAt(new Date().toLocaleTimeString());
    } finally { setSaving(false); }
  }

  return (
    <div data-testid="settings-page" className="max-w-3xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Admin</div>
        <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Company Settings</h1>
        <p className="text-sm text-[#68665E] mt-2">Used on the header of LR and Invoice PDFs.</p>
      </div>

      <form onSubmit={save} className="card-surface p-6 space-y-6">
        <div>
          <div className="section-title">Identity</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="input-label">Company Name</label><input className="input-field" value={form.name} onChange={(e)=>set("name", e.target.value)} data-testid="settings-name" /></div>
            <div><label className="input-label">Phone</label><input className="input-field" value={form.phone} onChange={(e)=>set("phone", e.target.value)} data-testid="settings-phone" /></div>
            <div className="md:col-span-2"><label className="input-label">Address</label><AddressAutocomplete className="input-field w-full" value={form.address} onChange={(v)=>set("address", v)} onSelect={(v)=>set("address", v)} testId="settings-address" /></div>
            <div><label className="input-label">Email</label><input type="email" className="input-field" value={form.email} onChange={(e)=>set("email", e.target.value)} data-testid="settings-email" /></div>
            <div><label className="input-label">GST Number</label><GstVerifyField value={form.gst_number} onChange={(v)=>set("gst_number", v)} address={form.address} onVerified={(d)=>{ if(d.pan && !form.pan_number) set("pan_number", d.pan); }} testId="settings-gst" /></div>
            <div><label className="input-label">PAN</label><input className="input-field font-mono" value={form.pan_number} onChange={(e)=>set("pan_number", e.target.value)} data-testid="settings-pan" /></div>
          </div>
        </div>

        <div>
          <div className="section-title">Bank — for Invoice Footer</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="input-label">Bank Name</label><input className="input-field" value={form.bank_name} onChange={(e)=>set("bank_name", e.target.value)} data-testid="settings-bank" /></div>
            <div><label className="input-label">Account No</label><input className="input-field font-mono" value={form.bank_account} onChange={(e)=>set("bank_account", e.target.value)} data-testid="settings-bank-acc" /></div>
            <div><label className="input-label">IFSC</label><input className="input-field font-mono" value={form.bank_ifsc} onChange={(e)=>set("bank_ifsc", e.target.value)} data-testid="settings-bank-ifsc" /></div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-[#E5E3DB]">
          {savedAt ? <div className="text-xs text-[#2D523B] flex items-center gap-2"><Check className="w-4 h-4" /> Saved at {savedAt}</div> : <div />}
          <button type="submit" disabled={saving} className="btn-primary" data-testid="settings-save-button">{saving ? "Saving..." : "Save Settings"}</button>
        </div>
      </form>

      <SystemHealth />
      <PublicExpenseLink />
      <ReminderNudges />
      <DataBackup />
      <DangerZone />
    </div>
  );
}

function DangerZone() {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const PHRASE = "DELETE ALL DATA";
  async function wipe() {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-all-data", { confirm: phrase.trim() });
      toast.success(`All data cleared — ${data.collections_cleared} tables reset. Reloading…`);
      setTimeout(() => { window.location.href = "/"; }, 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reset failed");
      setBusy(false);
    }
  }
  return (
    <div className="card-surface p-6 mt-8 border border-[#8A2526]/40" data-testid="danger-zone-card">
      <div className="section-title flex items-center gap-2 text-[#8A2526]"><AlertTriangle className="w-4 h-4" /> Danger Zone — Reset All Data</div>
      <p className="text-sm text-[#68665E] max-w-xl mb-3">Permanently deletes <strong>everything</strong> — LRs, consignments, invoices, quotations, expenses, bank statements, payments, bids, reminders, leads, the masters/directory, emails and documents. <strong>Only your login accounts are kept.</strong> This cannot be undone — take a Drive backup first.</p>
      {!open ? (
        <button className="btn-secondary !border-[#8A2526] !text-[#8A2526]" onClick={() => setOpen(true)} data-testid="danger-open-reset"><AlertTriangle className="w-4 h-4" /> Delete all data &amp; start fresh</button>
      ) : (
        <div className="border border-[#8A2526]/40 rounded p-4 bg-[#FBEDED]" data-testid="danger-confirm-box">
          <p className="text-sm text-[#8A2526] font-medium mb-2">Type <span className="font-mono">DELETE ALL DATA</span> to confirm.</p>
          <input className="input-field font-mono mb-3" value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder="DELETE ALL DATA" data-testid="danger-confirm-input" />
          <div className="flex gap-2">
            <button disabled={busy || phrase.trim() !== PHRASE} onClick={wipe} className="btn-primary !bg-[#8A2526] hover:!bg-[#6f1e1f]" data-testid="danger-confirm-btn">{busy ? "Deleting…" : "Permanently delete everything"}</button>
            <button className="btn-ghost" onClick={() => { setOpen(false); setPhrase(""); }} data-testid="danger-cancel-btn">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
